import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listScheduledTasks,
  getScheduledTask,
  createScheduledTask,
  updateScheduledTask,
  deleteScheduledTask,
  executeScheduledTask,
  pauseScheduledTask,
  resumeScheduledTask,
} from '~/data/endpoints/scheduled-tasks';
import type {
  CreateScheduledTaskBody,
  UpdateScheduledTaskBody,
  ListScheduledTasksParams,
} from '~/data/endpoints/scheduled-tasks';

export const scheduledTaskQueryKeys = {
  all: ['scheduled-tasks'] as const,
  list: (params: ListScheduledTasksParams) =>
    [...scheduledTaskQueryKeys.all, 'list', params.from ?? null, params.to ?? null] as const,
  detail: (id: string) => [...scheduledTaskQueryKeys.all, 'detail', id] as const,
};

/**
 * Fetches scheduled tasks. With no params (or an empty `{}`), returns the
 * full set of definitions for the redesigned Schedule list view; with
 * `from`/`to` the query is windowed for the legacy calendar view.
 */
export function useScheduledTasks(params: ListScheduledTasksParams = {}) {
  return useQuery({
    queryKey: scheduledTaskQueryKeys.list(params),
    queryFn: () => listScheduledTasks(params),
  });
}

export function useScheduledTask(id: string | undefined) {
  return useQuery({
    queryKey: scheduledTaskQueryKeys.detail(id ?? ''),
    queryFn: () => getScheduledTask(id as string),
    enabled: !!id,
  });
}

export function useCreateScheduledTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateScheduledTaskBody) => createScheduledTask(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduledTaskQueryKeys.all });
    },
  });
}

export function useUpdateScheduledTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateScheduledTaskBody }) => updateScheduledTask(id, body),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: scheduledTaskQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: scheduledTaskQueryKeys.detail(id) });
    },
  });
}

/**
 * Pauses a scheduled task — the row stays in place but its Cloud Scheduler
 * job is removed so it stops firing. Use {@link useResumeScheduledTask} to
 * re-arm later.
 */
export function usePauseScheduledTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pauseScheduledTask(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: scheduledTaskQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: scheduledTaskQueryKeys.detail(id) });
    },
  });
}

/**
 * Resumes a paused scheduled task; the API advances recurring tasks to the
 * next future occurrence.
 */
export function useResumeScheduledTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resumeScheduledTask(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: scheduledTaskQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: scheduledTaskQueryKeys.detail(id) });
    },
  });
}

/**
 * @deprecated The legacy "Cancel task" affordance hard-deleted the row.
 *  New code should call {@link usePauseScheduledTask} (resumable) or
 *  {@link useDeleteScheduledTask} (explicit destroy). This export is kept
 *  to avoid breaking older callers that haven't migrated yet.
 */
export function useCancelScheduledTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteScheduledTask(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: scheduledTaskQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: scheduledTaskQueryKeys.detail(id) });
    },
  });
}

export function useDeleteScheduledTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteScheduledTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduledTaskQueryKeys.all });
    },
  });
}

export function useExecuteScheduledTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => executeScheduledTask(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: scheduledTaskQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: scheduledTaskQueryKeys.detail(id) });
    },
  });
}
